<template>
  <my-giving-layout>
    <div class="w-full min-h-screen bg-[#F6F4F0]">
      <BackButton class="w-full px-[20px] md:px-[24px] lg:px-[28px] mb-[16px]" :customBack="back" />
      <InfoModal :is-open="showInfoModal" @close="showInfoModal = false" />
      <div class="flex flex-col px-[20px] md:px-[24px] lg:px-[28px]">
        <div class="flex flex-col">
          <div class="flex flex-row justify-between">
            <div
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem]"
            >
              Giving to
            </div>
            <div
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-bold"
            >
              {{ selectedGiveType }}
            </div>
          </div>
          <div class="flex flex-row justify-between pt-[8px]">
            <div
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem]"
            >
              Giving method
            </div>
            <div
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-bold"
            >
              {{ giveMethodLabel }}
            </div>
          </div>
        </div>
        <section v-if="showSelect">
          <div
            class="pt-[24px] text-[1.5rem] md:text-[2rem] lg:text-[2.25rem] leading-[2rem] md:leading-[2.5rem] lg:leading-[2.75rem] font-bold"
          >
            Enter amount
          </div>
          <div class="flex flex-row items-baseline">
            <div
              class="text-[2.25rem] md:text-[3rem] lg:text-[3.25rem] leading-[2.5rem] md:leading-[3.5rem] lg:leading-[4rem] font-black tracking-[-1.08px]"
              :class="toggleColor ? 'text-[#554638]' : 'text-[#A5A4A3]'"
            >
              $
            </div>
            <AmountInput
              v-model="selectData.givingAmount"
              :placeholder="amountPlaceholder"
              :disabled="!showSelect"
              :input-classes="toggleColor ? 'text-[#554638]' : 'text-[#A5A4A3]'"
              disabled-classes="disabled:text-[#554638] disabled:bg-[#F6F4F0]"
              @click="clearAmountPlaceholder"
              @blur="checkAmount"
              @input="handleAmountInput"
            />
          </div>
          <Divider :thickness="1" color="#63615E" class="pb-[32px]"></Divider>
          <p
            v-if="amountError"
            class="text-[#EC4E20] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] italic mt-[-28px] pb-[8px]"
          >
            Please enter a number
          </p>
          <BaseSelect
            v-if="showMonthSelect"
            v-model="selectData.monthOfGiving"
            class="w-full mb-[24px]"
            :options="monthOptions"
            label="Month of Giving"
            placeholder="Select a Month"
            width-classes="border-1 w-full border-[#C3C2C1] bg-[#FFFFFF] !h-full !p-[16px]"
            label-classes="w-full text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-semibold text-[#191817]"
            text-classes="w-full text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-normal text-[#21201E]"
          />
          <div v-if="showPIDfields">
            <DateOfBirth
              class="mb-[24px]"
              :dob-placeholder-text="dobPlaceholderText"
              :original-payload="originalPayload"
              @handle-form-data="updateDateOfBirth"
            />
            <PartialNRIC
              :partial-nric-placeholder-text="partialNricPlaceholderText"
              :original-payload="originalPayload"
              @on-info="showInfoModal = true"
              @handle-form-data="updatePartialNric"
            />
          </div>
          <BottomButton
            id="generate-qr-code"
            text="Generate QR Code"
            :disabled="isButtonDisabled"
            :loading="generating"
            @click="handleGenerate"
            class="w-full pt-[60px] mb-[32px]"
          />
        </section>
        <section v-if="!showSelect" class="pt-[32px]">
          <GivingSummary :data="summaryData" />
          <div class="vf-col-12">
            <PayNowQRCodeGenerator
              ref="payNowQRCodeGenerator"
              class="my-[32px]"
              :amount="selectData.givingAmount"
              :reference-text="referenceText"
              :giving-type="selectedGiveType"
              @qr-code-image="handleQrCodeImage"
            />
          </div>
          <BottomButton
            id="save-to-photos"
            text="Save to photos"
            :disabled="loading"
            @click="handleSave"
            class="w-full mb-[64px]"
          />
        </section>
      </div>
    </div>
    <Toast position="top-[80px]" />
  </my-giving-layout>
</template>
<script>
import BackButton from '@/components/common/BackButton.vue';
import AmountInput from '@/components/MyGiving/Common/AmountInput.vue';
import { sanitizeAmountInput } from '@/utils/amount-input';
import DateOfBirth from '@/components/MyGiving/Common/DateOfBirth.vue';
import PartialNRIC from '@/components/MyGiving/Common/PartialNRIC.vue';
import Divider from '@/components/design/Divider.vue';
import BaseSelect from '@/components/design/BaseSelect.vue';
import BottomButton from '@/components/common/BottomButton.vue';
import GivingSummary from '@/components/MyGiving/Common/GivingSummary.vue';
import PayNowQRCodeGenerator from '@/components/MyGiving/Common/PayNowQRCodeGenerator.vue';
import PayNowQRCodeGeneric from '@/components/MyGiving/Common/PayNowQRCodeGeneric.vue';
import InfoModal from '@/components/MyGiving/Common/InfoModal.vue';
import Toast from '@/components/common/Toast.vue';
import { churchTime } from '@/utils/date';
import { Media } from '@capacitor-community/media';
import { isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';
import { mapState, mapGetters } from 'vuex';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
export default {
  components: {
    BackButton,
    AmountInput,
    DateOfBirth,
    PartialNRIC,
    Divider,
    BaseSelect,
    BottomButton,
    GivingSummary,
    PayNowQRCodeGenerator,
    PayNowQRCodeGeneric,
    InfoModal,
    Toast,
    MyGivingLayout,
  },
  data() {
    return {
      showInfoModal: false,
      personalID: '',
      referenceText: 'OFFERING',
      amountPlaceholder: Number(0).toFixed(2),
      dobPlaceholderText: 'Enter your Date of Birth',
      partialNricPlaceholderText: 'Enter last 4 Char of NRIC / FIN',
      showMonthSelect: true,
      showPIDfields: true,
      showSelect: true,
      toggleColor: false,
      amountError: false,
      qrCodeImage: null,
      generating: false,
      loading: false,
      selectData: {
        givingAmount: null,
        monthOfGiving: null,
      },
      dateOfBirth: null,
      partialNric: null,
      summaryData: null,
      monthOptions: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
    };
  },
  ionViewWillEnter() {
    // Reset showSelect to show the form when navigating back to the page
    this.showSelect = true;

    // Reset input amount to 0, or to the one-time pledge the member just made to
    // this fund (BF2026), which they can still change.
    this.selectData.givingAmount = this.giveNowAmount;
    if (this.selectData.givingAmount) this.checkAmount();

    // Always use English locale to prevent Chinese characters
    const currentMonth = new Date().toLocaleString('en-US', {
      month: 'long',
    });
    this.selectData.monthOfGiving = currentMonth;
    if (this.selectedGiveType === 'Offering' || this.selectedGiveType === 'GenerationS Fund') {
      this.showMonthSelect = false;
    } else {
      this.showMonthSelect = true;
    }

    if (this.selectedGiveType === 'Offering') {
      this.showPIDfields = false;
    } else {
      this.showPIDfields = true;
    }

    const copyOriginalPayload = { ...this.originalPayload };
    this.partialNric = copyOriginalPayload?.partialNric;
    this.dateOfBirth = copyOriginalPayload?.dateOfBirth;
  },
  computed: {
    ...mapState('myGiving', ['selectedGiveType', 'selectedGiveMethod', 'selectedLinkedAccount']),
    ...mapGetters({
      originalPayload: 'profile/payload',
      giveNowAmount: 'myGiving/giveNowAmount',
    }),
    giveMethodLabel() {
      switch (this.selectedGiveMethod) {
        case 'paynow-qr':
          return 'PayNow QR';
        case 'credit-card':
          return 'Credit Card';
        case 'local-bank-transfer':
          return 'Local Bank Transfer';
        case 'overseas-banking':
          return 'Overseas Banking';
        default:
          return 'Select Method';
      }
    },
    isButtonDisabled() {
      const amount = this.selectData.givingAmount
        ? Number(this.selectData.givingAmount.replace(/,/g, ''))
        : 0;
      return !this.selectData.givingAmount || amount <= 0;
    },
  },
  methods: {
    back() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.replace('/my-giving');
      }
    },
    handleGenerate() {
      this.generating = true;
      if (this.isButtonDisabled) {
        return;
      }
      // Format amount for display
      const cleanAmount = this.selectData.givingAmount
        ? this.selectData.givingAmount.replace(/,/g, '')
        : '0.00';
      const formattedAmount = `$${Number(cleanAmount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

      if (this.selectedGiveType === 'GenerationS Fund') {
        this.summaryData = {
          Amount: formattedAmount,
          'Giving to': this.selectedGiveType,
          'Date of Birth': churchTime(this.dateOfBirth).format('DD-MM-YYYY'),
          'Last 4 chars of NRIC/FIN/Passport no.': this.partialNric,
        };
      } else if (this.selectedGiveType === 'Offering') {
        this.summaryData = {
          Amount: formattedAmount,
          'Giving to': this.selectedGiveType,
        };
      } else {
        this.summaryData = {
          Amount: formattedAmount,
          Month: this.selectData.monthOfGiving,
          'Giving to': this.selectedGiveType,
          'Date of Birth': churchTime(this.dateOfBirth).format('DD-MM-YYYY'),
          'Last 4 chars of NRIC/FIN/Passport no.': this.partialNric,
        };
      }

      // personal ID
      if (this.partialNric && this.dateOfBirth) {
        this.personalID = this.partialNric + churchTime(this.dateOfBirth).format('DDMM');
      }
      if (this.selectedGiveType === 'Offering') {
        this.referenceText = 'OFFERING';
      } else {
        this.referenceText = (
          this.personalID + this.shortenGivingType(this.selectedGiveType)
        ).toUpperCase();
      }
      this.showSelect = false;
    },
    handleQrCodeImage(image) {
      this.qrCodeImage = image;
      this.generating = false;
    },
    async handleSave() {
      this.loading = true;
      try {
        const highResDataUrl = await this.$refs.payNowQRCodeGenerator.generateHighRes();
        if (isIosNativeOnly()) {
          const opts = { path: highResDataUrl };
          await Media.savePhoto(opts);
        } else if (isAndroidNativeOnly()) {
          const albumName = 'MyGiving';
          // Get the list of all albums to find the one we want
          let albumIdentifier;
          const albumsResult = await Media.getAlbums();
          const targetAlbum = albumsResult.albums.find(album => album.name === albumName);
          if (targetAlbum) {
            // Album found, use its identifier
            albumIdentifier = targetAlbum.identifier;
          } else {
            // Album not found, so create a new one
            const createResult = await Media.createAlbum({ name: albumName });
            albumIdentifier = createResult.identifier;
          }
          const opts = { path: highResDataUrl, albumIdentifier: albumIdentifier };
          await Media.savePhoto(opts);
        }
        this.$store.dispatch('toast/success', 'Successfully saved QR code to your photos');
      } catch (e) {
        this.$store.dispatch('toast/error', 'Failed to download image');
      } finally {
        this.loading = false;
      }
    },
    shortenGivingType(type) {
      const monthText = this.selectData.monthOfGiving.substring(0, 3);
      switch (type) {
        case 'Tithe':
          return monthText + 'T';
        case 'Building Fund':
          return monthText + 'B';
        case 'Heart Community Services':
          return monthText + 'H';
        case 'Missions':
          return monthText + 'M';
        case 'GenerationS Fund':
          return 'GENS';
        default:
          return '';
      }
    },
    clearAmountPlaceholder() {
      this.amountPlaceholder = '';
      this.toggleColor = true;
    },
    handleAmountInput(event) {
      // Filter input in real-time (no comma formatting while typing)
      this.selectData.givingAmount = sanitizeAmountInput(event.target.value);
    },
    checkAmount() {
      if (
        !this.selectData.givingAmount ||
        Number(this.selectData.givingAmount.replace(/,/g, '')) <= 0
      ) {
        this.amountPlaceholder = Number(0).toFixed(2);
        this.toggleColor = false;
      } else {
        // 1. Sanitize the input by removing any commas
        const cleanInput = this.selectData.givingAmount.replace(/,/g, '');

        // check if cleanInput is a number
        if (isNaN(cleanInput)) {
          this.amountError = true;
        }

        if (cleanInput !== '' && !isNaN(cleanInput)) {
          // Format with commas and 2 decimal places
          const formattedNum = Number(cleanInput).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          this.selectData.givingAmount = formattedNum;
          this.amountError = false;
        }
        this.toggleColor = true;
      }
    },
    updatePartialNric(partialNric) {
      this.partialNric = partialNric;
    },
    updateDateOfBirth(dateOfBirth) {
      this.dateOfBirth = dateOfBirth;
    },
  },
};
</script>
